import {DatePicker, Form, Select, Tooltip} from 'antd';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import moment from 'moment';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {EXPORT_MINI_FEEDBACK_RESPONSES} from '../../resolvers';
import {isDabaEmail} from 'utils/helper';

export const ExportResponsesForm = ({feedbackId, featureId, closeModal}) => {
  const [form] = Form.useForm();

  const [exportResponses, {loading}] = useMutation(
    EXPORT_MINI_FEEDBACK_RESPONSES,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      onError: error => toast.error(error.message),
    },
  );

  const handleSubmit = values => {
    const emails = (values.emails || [])
      .map(email => email.trim())
      .filter(Boolean);

    const invalid = emails.filter(email => !isDabaEmail(email));
    if (invalid.length) {
      toast.error(
        `Use official daba emails only — invalid: ${invalid.join(', ')}`,
      );
      return;
    }

    exportResponses({
      variables: {
        input: {
          feedbackId: feedbackId || null,
          featureId: featureId || null,
          startDate: values.startDate
            ? moment(values.startDate).format()
            : null,
          endDate: values.endDate ? moment(values.endDate).format() : null,
          emails: emails.length ? emails : null,
        },
      },
    })
      .then(({data}) => {
        const result = data?.exportMiniFeedbackResponses;
        if (!result || result.__typename === 'Error') {
          toast.error(result?.message || 'Failed to export responses');
          return;
        }
        toast.success(result.message || 'Export sent');
        form.resetFields();
        closeModal();
      })
      .catch(error => toast.error(error.message));
  };

  return (
    <div>
      <h1 style={{fontSize: 24, marginBottom: 8}}>Export Responses</h1>
      <p style={{color: '#6b7280', marginBottom: 24}}>
        Responses for this feedback will be compiled and sent by email. Leave
        the dates empty to export all responses.
      </p>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="emails"
          label={
            <Tooltip
              trigger="hover"
              title="If no email is provided, it will be sent to the email that you logged in with">
              Recipient Emails (Optional)
            </Tooltip>
          }
          rules={[
            {
              validator: (_, value) => {
                const invalid = (value || [])
                  .map(email => email.trim())
                  .filter(email => email && !isDabaEmail(email));
                if (invalid.length) {
                  return Promise.reject(
                    new Error(
                      `Only daba emails are allowed — invalid: ${invalid.join(
                        ', ',
                      )}`,
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}>
          <Select
            mode="tags"
            allowClear
            open={false}
            tokenSeparators={[',', ' ']}
            placeholder="dabaadmin@joindaba.com"
          />
        </Form.Item>
        <Form.Item label="Start Date" name="startDate">
          <DatePicker
            style={{width: '100%'}}
            placeholder="Start Date"
            showTime
            allowClear
          />
        </Form.Item>
        <Form.Item label="End Date" name="endDate">
          <DatePicker
            style={{width: '100%'}}
            placeholder="End Date"
            showTime
            allowClear
          />
        </Form.Item>
        <SubmitButton
          label={loading ? 'Exporting...' : 'Export Responses'}
          disabled={loading}
        />
      </Form>
    </div>
  );
};
